import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Printer } from "lucide-react";

import { HoverHintTrigger } from "@/components/ui/title-help";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { zplPrintStationLabel, type PublicStationOption } from "@/lib/zplPrintLocation";
import { cn } from "@/lib/utils";

const INLINE_PRINTER_SELECT_MIN_WIDTH_PX = 220;

const HEADER_TRIGGER_CLASS = "min-w-[10rem] w-auto whitespace-nowrap";

/**
 * Header location control for floor label pages. Hub resolves the printer
 * from this station plus the page's stock size and DPI.
 */
export function InboundZplPrinterHeaderButton({
  station,
  onStationChange,
  unavailableMessage,
  onPopoverOpenChange,
  className,
}: {
  station: string;
  onStationChange: (station: string) => void;
  unavailableMessage?: string | null;
  onPopoverOpenChange?: (open: boolean) => void;
  className?: string;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastParentWidthRef = useRef<number | null>(null);
  const [inlineSelect, setInlineSelect] = useState(true);

  const handleListOpenChange = useCallback(
    (open: boolean) => {
      onPopoverOpenChange?.(open);
    },
    [onPopoverOpenChange],
  );

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (element == null) return;
    const parent = element.parentElement;
    if (parent == null) return;

    const measure = (): void => {
      const parentWidth = parent.getBoundingClientRect().width;
      const previousWidth = lastParentWidthRef.current;
      if (previousWidth != null && Math.abs(parentWidth - previousWidth) < 1) return;
      lastParentWidthRef.current = parentWidth;

      let usedBySiblings = 0;
      for (const child of parent.children) {
        if (child === element) continue;
        usedBySiblings += child.getBoundingClientRect().width;
      }
      const parentStyles = getComputedStyle(parent);
      const gapValue = parentStyles.columnGap || parentStyles.gap || "0";
      const gap = Number.parseFloat(gapValue) || 0;
      const gapCount = Math.max(0, parent.children.length - 1);
      const available = parentWidth - usedBySiblings - gap * gapCount;
      setInlineSelect(available >= INLINE_PRINTER_SELECT_MIN_WIDTH_PX);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  const stationsQuery = useQuery({
    queryKey: ["public-stations"],
    queryFn: () => apiFetch<{ stations: PublicStationOption[] }>("/api/zpl-printers/stations"),
    staleTime: 120_000,
  });
  const summary = zplPrintStationLabel(station, stationsQuery.data?.stations ?? []);
  const select = (
    <ZplLocationSelect
      id="inbound-header-zpl-location"
      label="Location"
      showLabel={false}
      size="compact"
      triggerMode={inlineSelect ? "labeled" : "icon"}
      triggerIcon={<Printer className="h-5 w-5 shrink-0" aria-hidden />}
      ariaSummary={summary}
      className="min-w-0"
      triggerClassName={inlineSelect ? HEADER_TRIGGER_CLASS : undefined}
      value={station}
      onChange={onStationChange}
      onListOpenChange={handleListOpenChange}
      unavailableMessage={inlineSelect ? unavailableMessage : null}
    />
  );

  return (
    <div
      ref={containerRef}
      className={cn("flex shrink-0 justify-end", inlineSelect ? "max-w-full" : "min-w-10", className)}
    >
      {!inlineSelect ? (
        <HoverHintTrigger align="end" openOnTriggerActivate closeOnTriggerActivate trigger={select}>
          {unavailableMessage != null && unavailableMessage !== "" ? unavailableMessage : summary}
        </HoverHintTrigger>
      ) : (
        select
      )}
    </div>
  );
}
